import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Briefcase, BookOpen, FileText, Users, Mail, 
  ClipboardList, GraduationCap, TrendingUp, ArrowRight, UserCheck
} from 'lucide-react';

const API = '/api';

export const AdminDashboard = () => {
  const { t, direction } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Briefcase, label: 'Active Jobs', value: stats?.active_jobs || 0, total: stats?.total_jobs || 0, href: '/admin/jobs', color: 'bg-blue-500' },
    { icon: BookOpen, label: 'Course Templates', value: stats?.total_courses || 0, href: '/admin/course-templates', color: 'bg-green-500' },
    { icon: FileText, label: 'New Applications', value: stats?.new_applications || 0, total: stats?.total_applications || 0, href: '/admin/applications', color: 'bg-purple-500' },
    { icon: UserCheck, label: 'Enrollments', value: stats?.pending_enrollments || 0, total: stats?.total_enrollments || 0, href: '/admin/enrollment-requests', color: 'bg-teal-500' },
    { icon: ClipboardList, label: 'Recruitment Requests', value: stats?.new_recruitment_requests || 0, total: stats?.recruitment_requests || 0, href: '/admin/recruitment-requests', color: 'bg-orange-500' },
    { icon: GraduationCap, label: 'Training Requests', value: stats?.new_training_requests || 0, total: stats?.training_requests || 0, href: '/admin/training-requests', color: 'bg-pink-500' },
    { icon: Mail, label: 'New Inquiries', value: stats?.new_inquiries || 0, total: stats?.total_inquiries || 0, href: '/admin/inquiries', color: 'bg-yellow-500' },
    { icon: Users, label: 'Talent Pool', value: stats?.talent_pool_size || 0, href: '/admin/talent-pool', color: 'bg-cyan-500' },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#111827] mb-2">{t('admin.dashboard')}</h1>
        <p className="text-[#6B7280]">Welcome back! Here's an overview of your platform.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.href}>
            <Card className="card-hover h-full" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-xl ${stat.color} bg-opacity-10 flex items-center justify-center`}>
                    <stat.icon className={`h-6 w-6 ${stat.color.replace('bg-', 'text-')}`} />
                  </div>
                  <ArrowRight className={`h-5 w-5 text-[#E5E7EB] ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                </div>
                <div className="mt-4">
                  <p className="text-3xl font-bold text-[#111827]">{stat.value}</p>
                  <p className="text-sm text-[#6B7280]">
                    {stat.label}
                    {stat.total !== undefined && stat.total !== stat.value && (
                      <span className="text-xs ms-1">/ {stat.total} total</span>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link 
              to="/admin/jobs" 
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAFB] transition-colors"
              data-testid="quick-action-jobs"
            >
              <div className="w-10 h-10 rounded-lg bg-[#33AADD]/10 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-[#33AADD]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#111827]">Manage Jobs</p>
                <p className="text-sm text-[#6B7280]">Add, edit, or close job listings</p>
              </div>
              <ArrowRight className={`h-5 w-5 text-[#E5E7EB] ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            </Link>
            <Link 
              to="/admin/course-templates" 
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAFB] transition-colors"
              data-testid="quick-action-courses"
            >
              <div className="w-10 h-10 rounded-lg bg-[#33AADD]/10 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-[#33AADD]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#111827]">Manage Course Templates</p>
                <p className="text-sm text-[#6B7280]">Add or update course content</p>
              </div>
              <ArrowRight className={`h-5 w-5 text-[#E5E7EB] ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            </Link>
            <Link 
              to="/admin/applications" 
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAFB] transition-colors"
              data-testid="quick-action-applications"
            >
              <div className="w-10 h-10 rounded-lg bg-[#33AADD]/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#33AADD]" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#111827]">Review Applications</p>
                <p className="text-sm text-[#6B7280]">Process candidate applications</p>
              </div>
              <ArrowRight className={`h-5 w-5 text-[#E5E7EB] ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Platform Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-sm text-green-700">System Online</span>
                </div>
                <span className="text-xs text-green-600">All services running</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#6B7280]">Active Job Listings</span>
                  <span className="font-medium text-[#111827]">{stats?.active_jobs || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#6B7280]">Published Courses</span>
                  <span className="font-medium text-[#111827]">{stats?.total_courses || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#6B7280]">Pending Reviews</span>
                  <span className="font-medium text-[#111827]">
                    {(stats?.new_applications || 0) + (stats?.new_inquiries || 0) + (stats?.new_recruitment_requests || 0) + (stats?.new_training_requests || 0)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
